import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardPage from '../components/DashboardPage';
import api from '../services/api';

const META = {
  enterprise_supplier_development: {
    subtitle: 'ESD - Enterprise Development and Supplier Development',
    scorecardPath: '/esd/enterprise-supplier-development/scorecard',
    scorecardDesc: 'Enterprise and Supplier Development scorecard data and compliance tracking',
    submitPath: '/esd/enterprise-supplier-development/submit-datasheet',
    submitDesc: 'Upload data sheet to calculate both ED and SD Scorecards',
  },
  enterprise_development_transport: {
    subtitle: 'ESD - Enterprise Development (Transport Road Freight)',
    scorecardPath: '/esd/enterprise-supplier-development/scorecard/enterprise-development-transport',
    scorecardDesc: 'Enterprise Development scorecard data and compliance tracking',
    submitPath: '/esd/enterprise-supplier-development/submit-datasheet/enterprise-development-transport',
    submitDesc: 'Upload data sheet to calculate ED Scorecard',
  },
}

const ESD_GROUP_KEYS = new Set(['enterprise_supplier_development', 'enterprise_development_transport']);

const EnterpriseSupplierDevelopment = () => {
  const navigate = useNavigate();
  const { elementKey: rawElementKey } = useParams();
  const elementKey = (rawElementKey || 'enterprise_supplier_development').replace(/-/g, '_');
  const meta = META[elementKey] || META.enterprise_supplier_development;

  const [title, setTitle] = useState('Enterprise and Supplier Development');

  useEffect(() => {
    api.get('/core/scorecard/list_elements/')
    .then(res => {
      const group = res.data.find(g => ESD_GROUP_KEYS.has(g.key));
      if (group?.label) setTitle(group.label);
    })
    .catch(() => {});
  }, []);


  const actions = [
    {
      label: 'ED & SD Scorecard',
      onClick: () => navigate(meta.scorecardPath),
      desc: meta.scorecardDesc,
      icon: (
        <svg viewBox="0 0 24 24">
          <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
          <rect x="9" y="3" width="6" height="4" rx="1"/>
          <line x1="9" y1="12" x2="15" y2="12"/>
          <line x1="9" y1="16" x2="13" y2="16"/>
        </svg>
      ),
    },
    {
      label: 'Submit Data',
      onClick: () => navigate(meta.submitPath),
      desc: meta.submitDesc,
      icon: (
        <svg viewBox="0 0 24 24">
          <line x1="22" y1="2" x2="11" y2="13"/>
          <polygon points="22 2 15 22 11 13 2 9 22 2"/>
        </svg>
      ),
    },
    {
      label: 'Reports',
      onClick: () => navigate('/reports/enterprise-development'),
      desc: 'Enterprise & Supplier Development reporting',
      icon: (
        <svg viewBox="0 0 24 24">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      ),
    },
    {
      label: 'Beneficiary Companies',
      desc: 'Manage enterprise development beneficiaries',
      icon: (
        <svg viewBox="0 0 24 24">
          <rect x="2" y="7" width="20" height="14" rx="2"/>
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        </svg>
      ),
    },
    {
      label: 'Contributions',
      onClick: () => navigate(`/esd/documentation/${elementKey}`),
      desc: 'Track monetary and non-monetary contributions',
      icon: (
        <svg viewBox="0 0 24 24">
          <line x1="12" y1="1" x2="12" y2="23"/>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      ),
    },
    {
      label: 'Loan Accounts',
      desc: 'Enterprise loan and interest management',
      icon: (
        <svg viewBox="0 0 24 24">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
      ),
    },
    {
      label: 'Grant Records',
      desc: 'Non-recoverable contribution records',
      icon: (
        <svg viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
    },
    {
      label: 'Supplier List',
      desc: 'Registered supplier directory',
      icon: (
        <svg viewBox="0 0 24 24">
          <ellipse cx="12" cy="5" rx="9" ry="3"/>
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
        </svg>
      ),
    },
    {
      label: 'Development Plans',
      desc: 'Supplier development roadmaps',
      icon: (
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
    },
    {
      label: 'Progress Tracking',
      desc: 'Monitor supplier growth milestones',
      icon: (
        <svg viewBox="0 0 24 24">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      ),
    },
  ];

  return (
    <DashboardPage
      theme={elementKey}
      title={title}
      subtitle={meta.subtitle}
      actions={actions}
    />
  );
};

export default EnterpriseSupplierDevelopment;
